/**
 * API reference content, transcribed from the live qost.ai/api-reference.
 *
 * The API key shown is the placeholder from that page, not a real credential.
 */

export const apiReference = {
  eyebrow: "API Reference",
  title: "Qost.ai API Reference",
  intro:
    "Integrate Qost.ai's cost data and recommendations directly into your internal tooling, dashboards, or FinOps workflows.",
  primaryCta: { label: "Get API Key", href: "https://app.qost.ai/register" },
  secondaryCta: { label: "Documentation", href: "/docs" },

  authHeading: "Authentication",
  authBody:
    "All API requests must include your API key in the `Authorization` header as a Bearer token. You can generate an API key from your account settings.",
  authTags: ["REST API", "JSON responses", "Bearer token auth"],
  authSample: `curl https://api.qost.ai/v1/savings \\
  -H "Authorization: Bearer qost_live_xxxxxxxxxxxx" \\
  -H "Content-Type: application/json"`,
  baseUrlLabel: "Base URL",
  baseUrl: "https://api.qost.ai/v1",
  baseUrlNote: "All endpoints are HTTPS only.",

  endpointsHeading: "Endpoints",
  endpoints: [
    {
      method: "GET",
      path: "/v1/accounts",
      description: "List all connected cloud accounts.",
      sample: `{
  "accounts": [
    {
      "id": "acc_01J2X...",
      "name": "Production AWS",
      "provider": "aws",
      "account_id": "123456789012",
      "connected_at": "2025-01-15T10:30:00Z",
      "status": "active"
    }
  ]
}`,
    },
    {
      method: "GET",
      path: "/v1/savings",
      description: "Retrieve identified savings opportunities across all connected accounts.",
      sample: `{
  "total_monthly_savings": 12450.00,
  "currency": "USD",
  "opportunities": [
    {
      "id": "opp_abc123",
      "type": "reserved_instance",
      "service": "EC2",
      "estimated_monthly_savings": 4200.00,
      "confidence": "high",
      "resource_id": "i-0abc123def456"
    }
  ]
}`,
    },
    {
      method: "GET",
      path: "/v1/reports/monthly",
      description: "Generate a monthly cost summary report for a given period.",
      sample: `{
  "period": "2025-02",
  "total_spend": 58240.00,
  "savings_realized": 14100.00,
  "savings_percentage": 24.2,
  "breakdown": {
    "EC2": 28000.00,
    "S3": 8200.00,
    "RDS": 12040.00,
    "other": 10000.00
  }
}`,
    },
    {
      method: "POST",
      path: "/v1/alerts",
      description: "Create a spend alert that fires when a threshold is exceeded.",
      sample: `{
  "alert": {
    "id": "alrt_xyz789",
    "name": "EC2 Anomaly Alert",
    "threshold_usd": 5000,
    "period": "daily",
    "channels": ["email", "slack"],
    "created_at": "2025-03-01T12:00:00Z",
    "status": "active"
  }
}`,
    },
    {
      method: "DELETE",
      path: "/v1/accounts/{id}",
      description: "Disconnect a cloud account and revoke all associated access.",
      sample: `{
  "success": true,
  "message": "Account disconnected successfully.",
  "account_id": "acc_01J2X..."
}`,
    },
  ],

  limitsHeading: "Rate Limits & Error Codes",
  rateLimits: [
    { plan: "Starter", limit: "100 requests / hour" },
    { plan: "Pro", limit: "1,000 requests / hour" },
    { plan: "Enterprise", limit: "Custom limits" },
  ],
  statusCodes: [
    { code: "200", meaning: "Success" },
    { code: "400", meaning: "Bad request / invalid parameters" },
    { code: "401", meaning: "Unauthorized — invalid API key" },
    { code: "403", meaning: "Forbidden — insufficient permissions" },
    { code: "429", meaning: "Rate limit exceeded" },
    { code: "500", meaning: "Internal server error" },
  ],

  customHeading: "Need a custom integration?",
  customBody:
    "Our Cloudlaya engineering team can help you build custom FinOps integrations, Terraform modules, or CI/CD cost gates.",
  customCta: { label: "Talk to an Engineer", href: "/contact" },
  customSecondaryCta: { label: "Full Documentation", href: "/docs" },
};
